import { useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
import { LoadingButton } from '@mui/lab';
import PropTypes from 'prop-types';
import { Stack, Typography, Box, TextField, Dialog, DialogContent, Checkbox, Autocomplete, CircularProgress } from '@mui/material';
import axios from '../../utils/axios';
import { objectToFormData } from '../../utils/objectToFormData';
import PurchasingGroupIntegrationsFrom from './PurchasingGroupIntegrationsFrom';
import updateItemInPlaceOrAppend from '../../utils/updateItemInPlaceOrAppend';

PurchasingGroupForm.propTypes = {
  netTerms: PropTypes.array,
  editPurchasingGroup: PropTypes.object,
  setPurchasingGroups: PropTypes.func,
  integrationTypes: PropTypes.array,
  open: PropTypes.bool,
  close: PropTypes.func
}

export default function PurchasingGroupForm({ netTerms, editPurchasingGroup, setPurchasingGroups, integrationTypes, open, close }) {
  const { enqueueSnackbar } = useSnackbar();

  const [submitting, setSubmitting] = useState(false);
  const [loadingGroup, setLoadingGroup] = useState(false);
  const [formValue, setFormValue] = useState({ integrations: [] });

  useEffect(() => {
    if (editPurchasingGroup && open) {
      setLoadingGroup(true);
      axios.get(`/purchasingGroup/purchasingGroup?id=${editPurchasingGroup.id}`)
        .then(res => setFormValue(res.data ?? { integrations: [] }))
        .catch(() => enqueueSnackbar('Get purchasing group data failed', { variant: 'error' }))
        .finally(() => setLoadingGroup(false));
    } else setFormValue({ integrations: [], netTermId: 2 });
  }, [open])

  const onSubmit = () => {
    setSubmitting(true);
    axios.post("/purchasingGroup/purchasingGroupForm", objectToFormData(formValue))
      .then((res) => {
        if (res.data) {
          enqueueSnackbar(`Purchasing group ${(editPurchasingGroup ? "updated" : "created")}`, { variant: 'success' });
          setPurchasingGroups(purchasingGroups => updateItemInPlaceOrAppend(purchasingGroups, res.data));
          close();
        }
        else enqueueSnackbar(`${(editPurchasingGroup ? "Update" : "Create")} purchasing group failed`, { variant: 'error' })
      })
      .catch(() => enqueueSnackbar(`${(editPurchasingGroup ? "Update" : "Create")} purchasing group failed`, { variant: 'error' }))
      .finally(() => setSubmitting(false));
  };

  return <Dialog open={open} onClose={close} maxWidth="md" >
    <DialogContent>
      {!loadingGroup ?<Stack spacing={1} minWidth="600px">
        <Box>
          <Typography variant="subtitle1">Purchasing Group Name*</Typography>
          <TextField
            name="name"
            size="small"
            placeholder="Enter Purchasing Group Name"
            fullWidth
            value={formValue.name ?? ""}
            onChange={(e) => setFormValue(form => ({ ...form, name: e.target.value }))}
          />
        </Box> 
        <Box>
          <Typography variant="subtitle1">Street</Typography>
          <TextField
            name="streetAddress"
            size="small"
            placeholder="Enter Street Address"
            fullWidth
            value={formValue.streetAddress ?? ""}
            onChange={(e) => setFormValue(form => ({ ...form, streetAddress: e.target.value }))}
          />
        </Box>
        <Box>
          <Typography variant="subtitle1">City</Typography>
          <TextField
            name="city"
            size="small"
            placeholder="Enter City"
            fullWidth
            value={formValue.city ?? ""}
            onChange={(e) => setFormValue(form => ({ ...form, city: e.target.value }))}
          />
        </Box>
        <Box>
          <Typography variant="subtitle1">State</Typography>
          <TextField
            name="state"
            size="small"
            placeholder="Enter State"
            fullWidth
            value={formValue.state ?? ""}
            onChange={(e) => setFormValue(form => ({ ...form, state: e.target.value }))}
          />
        </Box>
        <Box>
          <Typography variant="subtitle1">Zip</Typography>
          <TextField
            name="zip"
            size="small"
            placeholder="Enter Zip"
            fullWidth
            value={formValue.zip ?? ""}
            onChange={(e) => setFormValue(form => ({ ...form, zip: e.target.value }))}
          />
        </Box>
        <Box>
          <Typography variant="subtitle1">Phone Number</Typography>
          <TextField
            name="phoneNumber"
            size="small"
            placeholder="Enter Phone Number"
            fullWidth
            value={formValue.phoneNumber ?? ""}
            onChange={(e) => setFormValue(form => ({ ...form, phoneNumber: e.target.value }))}
          />
        </Box>
        <Box>
          <Typography variant="subtitle1">Net Terms</Typography>
          <Autocomplete
            fullWidth
            size="small"
            value={netTerms.find(netTerm => netTerm.value === formValue.netTermId) ?? (!editPurchasingGroup ? netTerms.find(netTerm => netTerm.value === 2) : null)}
            onChange={(event, val) => setFormValue(form => ({ ...form, netTermId: val?.value ?? 0 }))}
            options={netTerms}
            renderInput={(params) => <TextField {...params} placeholder="Net Terms" />}
          />
        </Box>
        <Box>
          <Checkbox checked={formValue.billFacilitiesToPurchasingGroupAddress ?? false}
            onChange={(e) => setFormValue(value => ({ ...value, billFacilitiesToPurchasingGroupAddress: e.target.checked }))} />
          Bill facilities under this purchasing group to the purchasing group address
        </Box>
        <Box>
          <Checkbox checked={formValue.billFacilityForTariffSurcharge ?? false}
            onChange={(e) => setFormValue(value => ({ ...value, billFacilityForTariffSurcharge: e.target.checked }))} />
          Bill facilities under this purchasing group for tariffs
        </Box>
        <Box>
          <Checkbox checked={formValue.noShippingChargeForInvoices ?? false}
            onChange={(e) => setFormValue(value => ({ ...value, noShippingChargeForInvoices: e.target.checked }))} />
          No shipping charge for invoices
        </Box>
        
        <PurchasingGroupIntegrationsFrom setFormValue={setFormValue} formValue={formValue} integrations={integrationTypes}
          title={`Integrations (${formValue.integrations?.length ?? 0})`} />
        <Box>
          <LoadingButton loading={submitting} disabled={!formValue?.name} sx={{ display: "flex", justifySelf: "center", mb: 1, mt: 1, px: 7 }}
            color="purple" size="medium" variant="contained" onClick={onSubmit}>
            Save
          </LoadingButton>
        </Box>
      </Stack> : <Box sx={{ display: "flex", justifyContent: "center", m: 3 }}><CircularProgress /></Box>}
    </DialogContent>
  </Dialog>;
}
